// Lead Score Card Component
// Displays lead scoring information with visual breakdown

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Target, Flame, TrendingUp } from 'lucide-react';

const gradeConfig = {
  A: { color: 'bg-emerald-500', textColor: 'text-emerald-600', bgColor: 'bg-emerald-50', borderColor: 'border-emerald-200', label: 'Hot Lead', icon: Flame },
  B: { color: 'bg-teal-500', textColor: 'text-teal-600', bgColor: 'bg-teal-50', borderColor: 'border-teal-200', label: 'Strong Lead', icon: TrendingUp },
  C: { color: 'bg-blue-500', textColor: 'text-blue-600', bgColor: 'bg-blue-50', borderColor: 'border-blue-200', label: 'Good Lead', icon: Target },
  D: { color: 'bg-amber-500', textColor: 'text-amber-600', bgColor: 'bg-amber-50', borderColor: 'border-amber-200', label: 'Fair Lead', icon: Target },
  F: { color: 'bg-slate-400', textColor: 'text-slate-600', bgColor: 'bg-slate-50', borderColor: 'border-slate-200', label: 'Low Priority', icon: Target }
};

const LeadScoreCard = ({ intake }) => {
  if (!intake.lead_score && intake.lead_score !== 0) return null;
  
  const config = gradeConfig[intake.lead_grade] || gradeConfig.C;
  const factors = intake.lead_score_factors || {};
  
  return (
    <Card className={`border-2 ${config.borderColor} ${config.bgColor}`} data-testid="lead-score-card">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Target className={`w-5 h-5 ${config.textColor}`} />
            Lead Score
          </div>
          <div className="flex items-center gap-2">
            {intake.lead_grade === 'A' && <Flame className="w-5 h-5 text-orange-500 animate-pulse" />}
            <div className={`w-10 h-10 rounded-full ${config.color} flex items-center justify-center`}>
              <span className="text-white text-lg font-bold">{intake.lead_grade}</span>
            </div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Score Display */}
        <div className="text-center py-2">
          <div className={`text-4xl font-bold ${config.textColor}`}>{intake.lead_score}</div>
          <div className="text-sm text-slate-600">{intake.lead_grade_label || config.label}</div>
        </div>
        
        {/* Score Breakdown */}
        {Object.keys(factors).length > 0 && (
          <div className="space-y-3 pt-2 border-t border-slate-200">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Score Breakdown</p>
            {Object.entries(factors).map(([key, factor]) => (
              <div key={key} className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{factor.label}</span>
                  <span className="font-medium text-slate-900">{factor.score}/{factor.max}</span>
                </div>
                <Progress 
                  value={(factor.score / factor.max) * 100} 
                  className="h-1.5"
                />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LeadScoreCard;
